import React, { useMemo } from "react";
import {
  Box,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import LineAreaChart from "components/charts/LineAreaChart";
import type { WineSalesMonthly } from "types/fulfillment";
import { formatCurrency } from "utils/formatCurrency";

function monthlyTotalsFromWineSeries(
  series: { name: string; data: number[] }[]
): number[] {
  if (!series.length) return [];
  const len = series[0].data.length;
  const totals = new Array<number>(len).fill(0);
  for (const s of series) {
    for (let i = 0; i < len; i++) {
      totals[i] += s.data[i] ?? 0;
    }
  }
  return totals;
}

const EMPTY_SERIES = [{ name: "Ganhos", data: [0, 0, 0, 0, 0, 0] }];

export type AreaTrendCardProps = {
  wineSalesMonthly: WineSalesMonthly | null;
  loading?: boolean;
};

export default function AreaTrendCard({
  wineSalesMonthly,
  loading,
  ...rest
}: AreaTrendCardProps & Record<string, unknown>) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const muted = useColorModeValue("secondaryGray.600", "secondaryGray.400");

  const [brand500, brand400, navy800, axisMuted] = useToken("colors", [
    "brand.500",
    "brand.400",
    "navy.800",
    "secondaryGray.600",
  ]);
  const areaGradientBottom = useColorModeValue("#FFFFFF", navy800);

  const chartData = useMemo(() => {
    if (!wineSalesMonthly?.series?.length) return EMPTY_SERIES;
    const data = monthlyTotalsFromWineSeries(wineSalesMonthly.series);
    return [{ name: "Ganhos", data }];
  }, [wineSalesMonthly]);

  const chartOptions = useMemo(() => {
    const categories = wineSalesMonthly?.categories ?? [];
    return {
      chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        redrawOnParentResize: true,
      },
      dataLabels: { enabled: false },
      stroke: { curve: "smooth" as const, width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          type: "vertical" as const,
          shadeIntensity: 1,
          inverseColors: false,
          colorStops: [
            [
              { offset: 0, color: brand400, opacity: 1 },
              { offset: 45, color: brand500, opacity: 0.55 },
              { offset: 100, color: areaGradientBottom, opacity: 1 },
            ],
          ],
        },
      },
      colors: [brand500],
      tooltip: {
        theme: "dark" as const,
        y: {
          formatter: (val: number) => formatCurrency(val),
        },
      },
      xaxis: {
        type: "category" as const,
        categories,
        labels: {
          style: {
            colors: axisMuted,
            fontSize: "12px",
            fontWeight: "500",
          },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false },
      legend: { show: false },
      grid: { show: false },
    };
  }, [wineSalesMonthly, brand500, brand400, areaGradientBottom, axisMuted]);

  return (
    <Card alignItems="center" flexDirection="column" w="100%" {...rest}>
      <Flex align="center" w="100%" px="15px" py="10px" flexWrap="wrap" gap={2}>
        <Box>
          <Text
            color={textColor}
            fontSize="xl"
            fontWeight="700"
            lineHeight="100%"
          >
            Evolução dos ganhos
          </Text>
          <Text fontSize="sm" color={muted} mt="4px">
            Faturamento mês a mês (últimos 6 meses, pedidos não cancelados)
          </Text>
        </Box>
      </Flex>
      <Box h="260px" w="100%" mt="auto">
        {loading || !wineSalesMonthly ? (
          <Flex minH="220px" align="center" justify="center">
            <Spinner color="brand.500" />
          </Flex>
        ) : (
          <LineAreaChart chartData={chartData} chartOptions={chartOptions} />
        )}
      </Box>
    </Card>
  );
}
